import type { TranslationKey } from '../i18n';
import type {
  ContestPhase,
  ParticipantStatus,
  PaymentChainStatus,
  PaymentStatus,
  PayoutStatus,
  QuestionKind,
  Rail,
} from '../types';
import type { BadgeTone } from '../components/ui/badge';

/**
 * Typed key maps.
 *
 * Enum values become translation keys through these tables rather than through a
 * template literal, so a missing translation is a compile error instead of a raw
 * `contest.phase.live` string leaking into the interface.
 */
export const PHASE_KEY: Record<ContestPhase, TranslationKey> = {
  draft: 'contest.phase.draft',
  scheduled: 'contest.phase.scheduled',
  registration_open: 'contest.phase.registration_open',
  registration_closed: 'contest.phase.registration_closed',
  live: 'contest.phase.live',
  awaiting_results: 'contest.phase.awaiting_results',
  results_ready: 'contest.phase.results_ready',
  cancelled: 'contest.phase.cancelled',
  voided: 'contest.phase.voided',
};

export const RAIL_KEY: Record<Rail, TranslationKey> = {
  fiat: 'rail.fiat',
  crypto: 'rail.crypto',
};

export const PARTICIPANT_KEY: Record<ParticipantStatus, TranslationKey> = {
  registered: 'participant.status.registered',
  awaiting_payment: 'participant.status.awaiting_payment',
  confirmed: 'participant.status.confirmed',
  active: 'participant.status.active',
  completed: 'participant.status.completed',
  disqualified: 'participant.status.disqualified',
  withdrawn: 'participant.status.withdrawn',
  refunded: 'participant.status.refunded',
};

export const PAYMENT_KEY: Record<PaymentStatus, TranslationKey> = {
  pending: 'payment.status.pending',
  confirmed: 'payment.status.confirmed',
  failed: 'payment.status.failed',
  expired: 'payment.status.expired',
  refunded: 'payment.status.refunded',
  cancelled: 'payment.status.cancelled',
};

/**
 * On-chain invoice states.
 *
 * Separate from `PAYMENT_KEY` because these are the blockchain's states rather than
 * the payment row's: an invoice can be `confirming` (money seen, not yet trusted),
 * which has no equivalent on the record.
 */
export const CHAIN_STATUS_KEY: Record<PaymentChainStatus, TranslationKey> = {
  awaiting_payment: 'pay.status.awaiting_payment',
  detected: 'pay.status.detected',
  confirming: 'pay.status.confirming',
  confirmed: 'pay.status.confirmed',
  underpaid: 'pay.status.underpaid',
  overpaid: 'pay.status.overpaid',
  expired: 'pay.status.expired',
  invalid: 'pay.status.invalid',
  failed: 'pay.status.failed',
};

export const PAYOUT_KEY: Record<PayoutStatus, TranslationKey> = {
  pending: 'payout.status.pending',
  approved: 'payout.status.approved',
  processing: 'payout.status.processing',
  completed: 'payout.status.completed',
  failed: 'payout.status.failed',
  cancelled: 'payout.status.cancelled',
};

export const KIND_KEY: Record<QuestionKind, TranslationKey> = {
  single_choice: 'admin.contests.kind.single_choice',
  multi_choice: 'admin.contests.kind.multi_choice',
  true_false: 'admin.contests.kind.true_false',
  numeric: 'admin.contests.kind.numeric',
};

export const TIE_BREAK_KEY: Record<'total_time' | 'earliest_completion', TranslationKey> = {
  total_time: 'admin.contests.tieBreak.total_time',
  earliest_completion: 'admin.contests.tieBreak.earliest_completion',
};

/**
 * Display helpers for the contest world.
 *
 * Deliberately NO arithmetic on money: the API sends decimal strings and the UI
 * only renders them. Any figure a participant sees was computed, rounded and
 * formatted on the server.
 */

/** Badge tone per lifecycle phase, so status is readable at a glance. */
export function phaseTone(phase: ContestPhase): BadgeTone {
  switch (phase) {
    case 'live':
      return 'success';
    case 'registration_open':
      return 'brand';
    case 'results_ready':
      return 'brand';
    case 'cancelled':
    case 'voided':
      return 'danger';
    case 'awaiting_results':
      return 'warning';
    case 'draft':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function participantTone(status: ParticipantStatus): BadgeTone {
  switch (status) {
    case 'confirmed':
    case 'active':
    case 'completed':
      return 'success';
    case 'awaiting_payment':
      return 'warning';
    case 'disqualified':
      return 'danger';
    case 'refunded':
      return 'brand';
    default:
      return 'neutral';
  }
}

export function paymentTone(status: PaymentStatus): BadgeTone {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'pending':
      return 'warning';
    case 'failed':
    case 'expired':
      return 'danger';
    case 'refunded':
      return 'brand';
    default:
      return 'neutral';
  }
}

export function payoutTone(status: PayoutStatus): BadgeTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'approved':
    case 'processing':
      return 'brand';
    case 'pending':
      return 'warning';
    case 'failed':
    case 'cancelled':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function railTone(rail: Rail): BadgeTone {
  return rail === 'crypto' ? 'brand' : 'neutral';
}

/** Human-readable duration from milliseconds, e.g. "1 h 04 min". */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0 s';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours} h ${String(minutes).padStart(2, '0')} min`;
  if (minutes > 0) return `${minutes} min ${String(seconds).padStart(2, '0')} s`;
  return `${seconds} s`;
}

/**
 * The server's clock, corrected for the round trip.
 *
 * The client timer is a DISPLAY only. Every deadline that matters is enforced by
 * the Worker, so a wrong device clock changes what the participant sees but never
 * what the server accepts.
 */
export function serverNowMs(serverTime: string, fetchedAtMs: number): number {
  const serverMs = Date.parse(serverTime);
  if (!Number.isFinite(serverMs)) return Date.now();
  return serverMs + (Date.now() - fetchedAtMs);
}

/** Countdown target for a contest, given the phase. */
export function countdownTarget(contest: {
  phase: ContestPhase;
  registrationOpensAt: string;
  registrationClosesAt: string;
  startsAt: string;
  endsAt: string;
}): { labelKey: TranslationKey; at: number } | null {
  switch (contest.phase) {
    case 'scheduled':
      return { labelKey: 'contest.countdown.opens', at: Date.parse(contest.registrationOpensAt) };
    case 'registration_open':
      return {
        labelKey: 'contest.countdown.closes',
        at: Date.parse(contest.registrationClosesAt),
      };
    case 'registration_closed':
      return { labelKey: 'contest.countdown.starts', at: Date.parse(contest.startsAt) };
    case 'live':
      return { labelKey: 'contest.countdown.ends', at: Date.parse(contest.endsAt) };
    default:
      return null;
  }
}
